import { isAdSlotBusy } from './adSlot';
import { logAd } from './adDiagnostics';
import {
  getAdsgramSequenceCooldownMs,
  isAdsgramSequenceActive,
  preloadAdsgramControllers,
  startAdsgramSequence,
} from './adsgramFlow';
import { hasActiveOfferVisit } from './offerVisit';
import { giftAPI } from '../services/api';
import { openGiftHandoffUrl } from './externalLink';

// Повторяем сценарий postingBot: после готовности UI выдерживаем чистую паузу,
// затем крутим цепочку rewarded → interstitial и повторяем её по кулдауну.
const STARTUP_DELAY_MS = 5_000;
const REPEAT_COOLDOWN_MS = 100_000;
const RETRY_DELAY_MS = 1_000;

let loopRunning = false;
let prefetchedGift: { id: number; url: string; tracking: boolean } | null = null;
let giftPrefetch: Promise<void> | null = null;
let prefetchedPriority: { id: number; url: string; tracking: boolean } | null = null;
let priorityPrefetch: Promise<void> | null = null;

function prefetchGift(): Promise<void> {
  if (giftPrefetch) return giftPrefetch;
  giftPrefetch = giftAPI.getAvailable().then((response) => {
    const url = response?.link?.trackingUrl || response?.link?.url;
    if (response?.success && response?.available && response?.link?.id && url) {
      prefetchedGift = {
        id: Number(response.link.id),
        url,
        tracking: Boolean(response.link.trackingUrl),
      };
    } else {
      prefetchedGift = null;
    }
  }).catch(() => {
    prefetchedGift = null;
  }).finally(() => {
    giftPrefetch = null;
  });
  return giftPrefetch;
}

function prefetchPriority(): Promise<void> {
  if (priorityPrefetch) return priorityPrefetch;
  priorityPrefetch = giftAPI.getRussiaPriority().then((response) => {
    const url = response?.link?.trackingUrl || response?.link?.url;
    prefetchedPriority = response?.success && response.available && response.link?.id && url
      ? { id: Number(response.link.id), url, tracking: Boolean(response.link.trackingUrl) }
      : null;
  }).catch(() => {
    prefetchedPriority = null;
  }).finally(() => {
    priorityPrefetch = null;
  });
  return priorityPrefetch;
}

async function redirectAfterAutomaticAds(): Promise<void> {
  try {
    if (!prefetchedPriority) await prefetchPriority();
    if (prefetchedPriority) {
      const priority = prefetchedPriority;
      prefetchedPriority = null;
      if (!priority.tracking) await giftAPI.trackClick(priority.id).catch(() => {});
      openGiftHandoffUrl(priority.url);
      return;
    }
    if (!prefetchedGift) await prefetchGift();
    if (prefetchedGift) {
      const gift = prefetchedGift;
      prefetchedGift = null;
      if (!gift.tracking) await giftAPI.trackClick(gift.id).catch(() => {});
      openGiftHandoffUrl(gift.url);
      return;
    }
    const subgram = await giftAPI.getOneSubgramSponsorLink().catch(() => null);
    if (subgram?.success && subgram.available && subgram.sponsor?.link) {
      openGiftHandoffUrl(subgram.sponsor.link);
      return;
    }
    openGiftHandoffUrl('https://t.me/your_stars_presents_bot?startapp');
  } catch {}
}

/**
 * Запускается один раз после загрузки UI. Возвращает функцию остановки.
 */
export function startAdsgramStartupLoop(): () => void {
  if (loopRunning) return () => {};
  loopRunning = true;

  let cancelled = false;
  let timer: number | undefined;
  // Цикл тикает раз в секунду — пишем причину простоя один раз, а не каждый тик.
  let hiddenSkipLogged = false;

  const schedule = (delay: number) => {
    if (cancelled) return;
    timer = window.setTimeout(attempt, delay);
  };

  const attempt = () => {
    if (cancelled) return;

    // Same shared cooldown used by postingBot for every AdsGram trigger.
    const cooldownLeft = getAdsgramSequenceCooldownMs();
    if (cooldownLeft > 0) {
      schedule(cooldownLeft);
      return;
    }

    // Экран может быть занят фоновым шедулером или цепочкой от кнопки пользователя —
    // тогда просто ждём и пробуем снова, а не показываем рекламу поверх рекламы.
    if (isAdsgramSequenceActive() || isAdSlotBusy()) {
      schedule(RETRY_DELAY_MS);
      return;
    }

    // На мобиле скрипт совместимости держит документ «видимым», поэтому цепочка
    // продолжает крутиться в свёрнутом приложении — это и есть работа в фоне.
    // Здесь мы отсекаем только честно скрытую вкладку без ухода по нашему офферу
    // (десктоп/веб, где скрипт совместимости не выдаётся).
    if (document.visibilityState === 'hidden' && !hasActiveOfferVisit()) {
      if (!hiddenSkipLogged) {
        hiddenSkipLogged = true;
        logAd('warn', 'loop', 'пропуск: окно скрыто и нет перехода по офферу');
      }
      schedule(RETRY_DELAY_MS);
      return;
    }
    hiddenSkipLogged = false;

    void prefetchGift();
    void prefetchPriority();
    const handoff = () => { void redirectAfterAutomaticAds(); };
    const launched = startAdsgramSequence({
      trigger: 'startup_auto',
      onInterstitialUnavailable: handoff,
      onInterstitialFinished: handoff,
    });
    if (!launched) handoff();
    schedule(launched ? REPEAT_COOLDOWN_MS : RETRY_DELAY_MS);
  };

  // Прогреваем оба контроллера в спокойном окне до первого показа.
  logAd('info', 'loop', `UI готов, первый показ через ${STARTUP_DELAY_MS / 1000}с`);
  void preloadAdsgramControllers();
  void prefetchGift();
  void prefetchPriority();
  schedule(STARTUP_DELAY_MS);

  return () => {
    cancelled = true;
    if (timer) window.clearTimeout(timer);
    loopRunning = false;
  };
}
